import { API, Auth } from 'aws-amplify';
import React, { useEffect } from 'react';
import { useParams } from 'react-router';
import { RootState, useDispatch, useSelector } from 'src/store';
import { buttonsReset } from 'src/store/modules/buttons';
import { editUserCompany } from 'src/store/modules/client';
import { editTextReset } from 'src/store/modules/editText';
import { imageReset } from 'src/store/modules/imageUpload';
import { trackingReset } from 'src/store/modules/trackings';
import { changeEnter } from 'src/utils/changeEnter';
import { getFontURL } from 'src/utils/getFontUrl';
import setInitValue from 'src/utils/setInitValue';
import { initTrackingValues } from '../TemplateTracking/CardModalTracking';

const initViewValues = {
  bg: 'color',
  bgOpacity: { bgDimmed: 30 },
  bgColor: { background: '#ffffff' },
  radius: { background: 10, close_button: 0, button: 4 },
  deeplink: {
    background: 'https://www.inappmessage.com/'
  },
  text: {
    title: '💌 Invitation 💌',
    sub_title:
      "We prepared an online party for our 30th anniversary. Just talk about the service and we'll give you a gift! See you at the party💖"
  },
  fontSize: { title: 2, sub_title: 0.8125 },
  fontWeight: { title: 500, sub_title: 400 },
  color: { title: '#000000', sub_title: '#000000' },
  fontFamily: { title: 'Inter', sub_title: 'Inter' },
  textAlign: { title: 'center', sub_title: 'center' },
  btnText: { close_button: 'Close', button: 'Accept' },
  btnFontFamily: { close_button: 'Inter', button: 'Inter' },
  btnTextAlign: { close_button: 'center', button: 'center' },
  btnFontSize: { close_button: 1, button: 1 },
  btnFontWeight: { close_button: 500, button: 500 },
  btnColor: { close_button: '#acacac', button: '#ffffff' },
  btnBgColorR: { close_button: 255, button: 243 },
  btnBgColorG: { close_button: 255, button: 87 },
  btnBgColorB: { close_button: 255, button: 110 },
  btnBgColorA: { close_button: 1, button: 1 },
  btnAgain: { type: 'none', close_button: "don't show" },
  button1: { close_button: [], button: [] },
  button2: { close_button: [], button: [] }
};

const CardModal = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    async function getUserId() {
      try {
        const current = await Auth.currentUserInfo();
        dispatch(editUserCompany(current.attributes['custom:Company']));
      } catch (error) {
        console.log('error');
      }
    }
    getUserId();
  }, []);

  const bgColor = useSelector((state: RootState) => state.imageUpload.bgColor);
  const radius = useSelector((state: RootState) => state.imageUpload.radius);
  const text = useSelector((state: RootState) => state.editText.text);
  const fontSize = useSelector((state: RootState) => state.editText.fontSize);
  const fontWeight = useSelector(
    (state: RootState) => state.editText.fontWeight
  );
  const color = useSelector((state: RootState) => state.editText.color);
  const fontFamily = useSelector(
    (state: RootState) => state.editText.fontFamily
  );
  const textAlign = useSelector((state: RootState) => state.editText.textAlign);
  const bgOpacity = useSelector(
    (state: RootState) => state.imageUpload.bgImageBlur
  );
  const btnText = useSelector((state: RootState) => state.buttons.btnText);
  const btnFontSize = useSelector(
    (state: RootState) => state.buttons.btnFontSize
  );
  const btnFontWeight = useSelector(
    (state: RootState) => state.buttons.btnFontWeight
  );
  const btnColor = useSelector((state: RootState) => state.buttons.btnColor);
  const btnBgColorR = useSelector(
    (state: RootState) => state.buttons.btnBgColorR
  );
  const btnBgColorG = useSelector(
    (state: RootState) => state.buttons.btnBgColorG
  );
  const btnBgColorB = useSelector(
    (state: RootState) => state.buttons.btnBgColorB
  );
  const btnBgColorA = useSelector(
    (state: RootState) => state.buttons.btnBgColorA
  );
  const btnFontFamily = useSelector(
    (state: RootState) => state.buttons.btnFontFamily
  );

  const backgroundDimmed = bgOpacity?.bgDimmed / 100;
  const displayValue = backgroundDimmed >= 0.01 ? backgroundDimmed : 0.3;

  const titleFontURL = getFontURL(fontFamily?.title);
  const subTitleFontURL = getFontURL(fontFamily?.sub_title);
  const btnFontURL = getFontURL(fontFamily?.button);
  const closeBtnFontURL = getFontURL(fontFamily?.close_button);

  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    async function onLoad() {
      try {
        if (id) {
          const { customValues } = await API.get(
            'iam-editor',
            `/templates/${id}`,
            ''
          );
          setInitValue(dispatch, customValues);
        }
      } catch (error) {}
    }
    setInitValue(dispatch, { ...initViewValues, ...initTrackingValues });

    onLoad();

    return () => {
      dispatch(buttonsReset());
      dispatch(editTextReset());
      dispatch(imageReset());
      dispatch(trackingReset());
    };
  }, []);

  const style = `
    @font-face {
      font-family: 'title';
      src: url(${titleFontURL});
    }
    @font-face {
      font-family: 'subTitle';
      src: url(${subTitleFontURL});
    }
    @font-face {
      font-family: 'btn';
      src: url(${btnFontURL});
    }
    @font-face {
      font-family: 'closeBtn';
      src: url(${closeBtnFontURL});
    }
    .root-view {
      width: 100%;
      height: 100%;
      position: relative;
      background: none;
    }

    .background {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      background: rgba(0, 0, 0, ${displayValue});
    }

    .modal-wrapper {
      width: 314px;
      height: auto;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      padding: 32px 20px 20px 20px;
      border-radius: ${radius?.background}px;
      background:${bgColor?.background};
    }

    .content-wrapper {
      width: 100%;
      height: auto;
      display: flex;
      flex-direction: column;
      background-color: transparent;
      margin-bottom: 24px;
      gap: 10px;
    }

    .content-wrapper .title {
      width: 100%;
      min-height: 48px;
      height: auto;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .content-wrapper .title p {
      width: 100%;
      font-size: ${fontSize?.title}rem;
      font-weight: ${fontWeight?.title};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          fontFamily?.title
        )
          ? fontFamily?.title
          : 'title'
      };
      overflow: hidden;
      text-align: ${textAlign?.title};
      color: ${color?.title};
    }

    .content-wrapper .sub-title {
      width: 100%;
      min-height: 56px;
      height: auto;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .content-wrapper .sub-title p {
      width: 100%;
      font-size: ${fontSize?.sub_title}rem;
      font-weight: ${fontWeight?.sub_title};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          fontFamily?.sub_title
        )
          ? fontFamily?.sub_title
          : 'subTitle'
      };
      text-align: ${textAlign?.sub_title};
      line-height: 1.4;
      color: ${color?.sub_title};
    }

    .btn-wrapper {
      width: 100%;
      height: 48px;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      gap: 8px;
    }

    .btn-close {
      width: 50%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      background-color: rgba(${btnBgColorR?.close_button}, ${
    btnBgColorG?.close_button
  }, ${btnBgColorB?.close_button}, ${btnBgColorA?.close_button});
      border-radius: ${radius?.close_button}px;
    }

    .btn-close p {
      font-size: ${btnFontSize?.close_button}rem;
      font-weight: ${btnFontWeight?.close_button};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          btnFontFamily?.close_button
        )
          ? btnFontFamily?.close_button
          : 'closeBtn'
      };
      color: ${btnColor?.close_button};
    }

    .btn-inner {
      width: 50%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      background-color: rgba(${btnBgColorR?.button}, ${btnBgColorG?.button}, ${
    btnBgColorB?.button
  }, ${btnBgColorA?.button});
      border-radius: ${radius?.button}px;
    }

    .btn-inner p {
      font-size: ${btnFontSize?.button}rem;
      font-weight: ${btnFontWeight?.button};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          btnFontFamily?.button
        )
          ? btnFontFamily?.button
          : 'btn'
      };
      color: ${btnColor?.button};
    }

    @media screen and (min-width: 355px) {
      .modal-wrapper {
        width: 314px;
      }
    }
  `;
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: style }} />
      <div className="root-view">
        <div className="background" />
        <div className="modal-wrapper">
          <div className="content-wrapper">
            <div className="title">
              <p>{changeEnter(text?.title)}</p>
            </div>
            <div className="sub-title">
              <p>{changeEnter(text?.sub_title)}</p>
            </div>
          </div>
          <div className="btn-wrapper">
            <div className="btn-close">
              <p>{btnText?.close_button}</p>
            </div>
            <div className="btn-inner">
              <p>{btnText?.button}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CardModal;
